import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import {
  createRecipe,
  createRecipeModifier,
  deleteRecipeModifier,
  fetchItemStock,
  fetchRecipe,
  fetchRecipeModifiers,
  fetchRecipes,
  fetchStockItem,
  updateRecipe,
  updateRecipeModifier,
} from '../api'
import type {
  CreateRecipeBody,
  CreateRecipeModifierBody,
  Recipe,
  StockItem,
  UpdateRecipeBody,
  UpdateRecipeModifierBody,
} from '../types'
import { inventoryKey } from './use-inventory'

const recipesKey = [...inventoryKey, 'recipes'] as const

function recipeKey(id: string) {
  return [...recipesKey, 'one', id] as const
}

/**
 * One recipe by id, for its editor page.
 *
 * Not refetched on focus: the editor compares what comes back against the
 * version it is editing, and a refetch the merchant did not ask for should not
 * be the thing that tells them someone else saved. It still refreshes after any
 * recipe write here, which is when a newer version can actually appear.
 */
export function useRecipe(id: string | null) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: recipeKey(id as string),
    queryFn: () => fetchRecipe(id as string),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  })

  return {
    recipe: data ?? null,
    isLoading: Boolean(id) && isPending,
    error,
    retry: () => void refetch(),
  }
}

/** One item's stock at each location. Only fetched once its detail is opened. */
export function useItemStock(itemId: string | null) {
  const { data, isPending, error } = useQuery({
    queryKey: [...inventoryKey, 'stock', 'item', itemId],
    queryFn: () => fetchItemStock(itemId as string),
    enabled: Boolean(itemId),
  })

  return { stock: data ?? null, isLoading: Boolean(itemId) && isPending, error }
}

/**
 * The query for one item's recipe, as the detail's section reads it.
 *
 * Asked by item rather than by recipe id because the list row carries no recipe
 * id: an item has at most one, so the filtered list answers with 0 or 1 rows.
 */
function itemRecipeQuery(itemId: string) {
  return queryOptions({
    queryKey: [...recipesKey, 'of', itemId],
    queryFn: () => fetchRecipes({ stockItemId: itemId, limit: 1, offset: 0 }),
  })
}

/** The recipe of one COMPOSITE item, or `null` when it has none. */
export function useItemRecipe(itemId: string | null) {
  const { data, isPending, error, refetch } = useQuery({
    ...itemRecipeQuery(itemId as string),
    enabled: Boolean(itemId),
  })

  return {
    recipe: data?.rows[0] ?? null,
    isLoading: Boolean(itemId) && isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * The recipes of these COMPOSITE items, by item id — an item without one is left
 * out. One request each, sharing `useItemRecipe`'s cache, so keep the list short.
 */
export function useItemRecipes(itemIds: readonly string[]) {
  return useQueries({
    queries: itemIds.map((id) => itemRecipeQuery(id)),
    combine: recipesByItem,
  })
}

/** At module level, for the same reason as `itemsById`. */
function recipesByItem(results: { data?: { rows: Recipe[] }; isPending: boolean }[]) {
  const byItem = new Map<string, Recipe>()
  for (const result of results) {
    const recipe = result.data?.rows[0]
    if (recipe) byItem.set(recipe.stockItemId, recipe)
  }
  return {
    recipes: byItem as ReadonlyMap<string, Recipe>,
    isLoading: results.some((result) => result.isPending),
  }
}

/** The recipes that use an item as an ingredient — where it goes. */
export function useRecipesUsing(itemId: string | null) {
  const { data, isPending, error } = useQuery({
    queryKey: [...recipesKey, 'using', itemId],
    queryFn: () =>
      fetchRecipes({ ingredientId: itemId as string, limit: PAGE_LIMIT_MAX, offset: 0 }),
    enabled: Boolean(itemId),
  })

  return { recipes: data?.rows ?? [], isLoading: Boolean(itemId) && isPending, error }
}

/**
 * Stock items by id, for naming a recipe's ingredients.
 *
 * A line carries only its ingredient's id. There is no batch read by ids, so
 * each is its own request — a recipe runs to a handful of lines, and each item
 * is cached under its id, so reopening a recipe asks for nothing new.
 */
export function useStockItemsById(ids: readonly string[]) {
  return useQueries({
    queries: ids.map((id) => stockItemQuery(id)),
    combine: itemsById,
  })
}

/** One stock item, read by its id and cached under it, as `useStockItemsById` shares. */
function stockItemQuery(id: string) {
  return queryOptions({
    queryKey: [...inventoryKey, 'item', id],
    queryFn: () => fetchStockItem(id),
    staleTime: 60_000,
  })
}

/**
 * The loaded items by id. At module level, not inline: TanStack Query reuses the
 * last combined result only while `combine` is the same function, so an inline
 * one built a new Map every render and re-ran every memo downstream of it.
 */
function itemsById(results: { data?: StockItem }[]): ReadonlyMap<string, StockItem> {
  const byId = new Map<string, StockItem>()
  for (const result of results) if (result.data) byId.set(result.data.id, result.data)
  return byId
}

/** One stock item, with its loading and failure — for a page that cannot go on without it. */
export function useStockItem(id: string | null) {
  const { data, isPending, error, refetch } = useQuery({
    ...stockItemQuery(id as string),
    enabled: Boolean(id),
  })

  return {
    item: data ?? null,
    isLoading: Boolean(id) && isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useRecipeActions() {
  const queryClient = useQueryClient()

  /* A recipe is read by its item and by each ingredient it uses, so every
     recipe query is refreshed rather than guessing which ones changed. The saved
     recipe goes straight into its own query first, so its page shows the new
     version without waiting for the refetch.

     A failure refreshes too: a 409 means a newer version exists, and the editor
     can only offer it once it has been read. */
  const save = useMutation({
    mutationFn: (
      input:
        | { kind: 'create'; body: CreateRecipeBody; idempotencyKey: string }
        | { kind: 'update'; id: string; body: UpdateRecipeBody },
    ): Promise<Recipe> =>
      input.kind === 'create'
        ? createRecipe(input.body, input.idempotencyKey)
        : updateRecipe(input.id, input.body),
    onSuccess: (recipe) => queryClient.setQueryData(recipeKey(recipe.id), recipe),
    onSettled: () => queryClient.invalidateQueries({ queryKey: recipesKey }),
  })

  return { save }
}

export function useRecipeModifierActions() {
  const queryClient = useQueryClient()

  /* Every recipe query, not just this recipe's modifiers: any modifier write
     bumps its recipe's version, and a recipe cached at the old one would be
     refused the next time it is saved. */
  const refresh = () => queryClient.invalidateQueries({ queryKey: recipesKey })

  const create = useMutation({
    mutationFn: ({
      recipeId,
      body,
      idempotencyKey,
    }: {
      recipeId: string
      body: CreateRecipeModifierBody
      idempotencyKey: string
    }) => createRecipeModifier(recipeId, body, idempotencyKey),
    onSuccess: refresh,
  })

  const update = useMutation({
    mutationFn: ({
      recipeId,
      modifierId,
      body,
    }: {
      recipeId: string
      modifierId: string
      body: UpdateRecipeModifierBody
    }) => updateRecipeModifier(recipeId, modifierId, body),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: ({ recipeId, modifierId }: { recipeId: string; modifierId: string }) =>
      deleteRecipeModifier(recipeId, modifierId),
    onSuccess: refresh,
  })

  return { create, update, remove }
}

/**
 * One page of every recipe, for the Recipes tab — or, given an item, just that
 * item's recipe (an item has at most one).
 */
export function useRecipeList(page: number, pageSize: number, stockItemId: string | null = null) {
  // Left off when unset: the service answers an empty filter with a 400.
  const query = {
    limit: pageSize,
    offset: page * pageSize,
    ...(stockItemId ? { stockItemId } : {}),
  }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...recipesKey, 'list', query],
    queryFn: () => fetchRecipes(query),
    placeholderData: (previous) => previous,
  })

  return {
    recipes: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * Every recipe, as the Modifiers tab's picker lists them.
 *
 * One page of the maximum: modifiers belong to a recipe and there is no read
 * across them, so the tab has to be told which recipe to look at.
 */
export function useRecipeOptions() {
  const { data, isPending, error } = useQuery({
    queryKey: [...recipesKey, 'options'],
    queryFn: () => fetchRecipes({ limit: PAGE_LIMIT_MAX, offset: 0 }),
    staleTime: 60_000,
  })

  return { recipes: data?.rows ?? [], isLoading: isPending, error }
}

/** One page of one recipe's modifiers. Held back until a recipe is chosen. */
export function useRecipeModifiers(recipeId: string | null, page: number, pageSize: number) {
  const query = { limit: pageSize, offset: page * pageSize }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...recipesKey, 'modifiers', recipeId, query],
    queryFn: () => fetchRecipeModifiers(recipeId as string, query),
    enabled: Boolean(recipeId),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === recipeId ? previous : undefined,
  })

  return {
    modifiers: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: Boolean(recipeId) && isPending,
    isRefreshing: isFetching && !isPending,
    error: recipeId ? error : null,
    retry: () => void refetch(),
  }
}
